import { useCallback, useEffect, useState } from 'react';

import type { ExplorationFilter } from '../../../types/exploration';
import type { SelectOption } from '~/shared/utils/types';

import {
  type CapacityBounds,
  type ExplorationFilterPanelState,
  explorationFiltersToPanelState,
  emptyPanelState,
  panelStateToExplorationFilters,
} from '../utils/filterConverters';

interface UseExplorationFilterStateProps {
  open: boolean;
  appliedFilters: ExplorationFilter[];
  capacityBounds: CapacityBounds;
}

export function useExplorationFilterState({
  open,
  appliedFilters,
  capacityBounds,
}: UseExplorationFilterStateProps) {
  const [pendingActivationStatuses, setPendingActivationStatuses] = useState<string[]>([]);
  const [pendingSupportStatuses, setPendingSupportStatuses] = useState<string[]>([]);
  const [pendingAccountFilters, setPendingAccountFilters] = useState<SelectOption[]>([]);
  const [pendingClusterFilters, setPendingClusterFilters] = useState<SelectOption[]>([]);
  const [pendingInfrastructureFilters, setPendingInfrastructureFilters] = useState<string[]>([]);
  const [pendingRegionFilters, setPendingRegionFilters] = useState<string[]>([]);
  const [pendingNodeGroupFilters, setPendingNodeGroupFilters] = useState<string[]>([]);
  const [pendingDiskTypeFilters, setPendingDiskTypeFilters] = useState<string[]>([]);
  const [pendingInstanceFilters, setPendingInstanceFilters] = useState<SelectOption[]>([]);
  const [pendingVolumeIdFilters, setPendingVolumeIdFilters] = useState<SelectOption[]>([]);
  const [pendingTagFilters, setPendingTagFilters] = useState<SelectOption[]>([]);
  const [pendingCapacityMin, setPendingCapacityMin] = useState(capacityBounds.min);
  const [pendingCapacityMax, setPendingCapacityMax] = useState(capacityBounds.max);
  const [lastSyncedOpen, setLastSyncedOpen] = useState(open);

  const syncFromApplied = useCallback(() => {
    const panel = explorationFiltersToPanelState(appliedFilters, capacityBounds);
    setPendingActivationStatuses(panel.activationStatuses);
    setPendingSupportStatuses(panel.supportStatuses);
    setPendingAccountFilters(panel.accountFilters);
    setPendingClusterFilters(panel.clusterFilters);
    setPendingInfrastructureFilters(panel.infrastructureFilters);
    setPendingRegionFilters(panel.regionFilters);
    setPendingNodeGroupFilters(panel.nodeGroupFilters);
    setPendingDiskTypeFilters(panel.diskTypeFilters);
    setPendingInstanceFilters(panel.instanceFilters);
    setPendingVolumeIdFilters(panel.volumeIdFilters);
    setPendingTagFilters(panel.tagFilters);
    setPendingCapacityMin(panel.capacityMin);
    setPendingCapacityMax(panel.capacityMax);
  }, [appliedFilters, capacityBounds]);

  useEffect(() => {
    if (open && !lastSyncedOpen) {
      syncFromApplied();
    }
    setLastSyncedOpen(open);
  }, [open, lastSyncedOpen, syncFromApplied]);

  useEffect(() => {
    if (open) {
      return;
    }

    // No active capacity filter: fully resync to the current bounds (not just clamp
    // downward) so a widened range (e.g. volumes finish loading after this hook's
    // initial {0,1}-fallback bounds) doesn't get misread as a user-narrowed range and
    // auto-open the Capacity section.
    const hasCapacityFilter = appliedFilters.some((f) => f.type === 'capacity');
    if (!hasCapacityFilter) {
      setPendingCapacityMin(capacityBounds.min);
      setPendingCapacityMax(capacityBounds.max);
      return;
    }

    setPendingCapacityMin((prev) => (prev < capacityBounds.min ? capacityBounds.min : prev));
    setPendingCapacityMax((prev) => (prev > capacityBounds.max ? capacityBounds.max : prev));
  }, [capacityBounds.min, capacityBounds.max, open, appliedFilters]);

  const getPanelState = useCallback((): ExplorationFilterPanelState => {
    return {
      activationStatuses: pendingActivationStatuses,
      supportStatuses: pendingSupportStatuses,
      accountFilters: pendingAccountFilters,
      clusterFilters: pendingClusterFilters,
      infrastructureFilters: pendingInfrastructureFilters,
      regionFilters: pendingRegionFilters,
      nodeGroupFilters: pendingNodeGroupFilters,
      diskTypeFilters: pendingDiskTypeFilters,
      instanceFilters: pendingInstanceFilters,
      volumeIdFilters: pendingVolumeIdFilters,
      tagFilters: pendingTagFilters,
      capacityMin: pendingCapacityMin,
      capacityMax: pendingCapacityMax,
    };
  }, [
    pendingActivationStatuses,
    pendingSupportStatuses,
    pendingAccountFilters,
    pendingClusterFilters,
    pendingInfrastructureFilters,
    pendingRegionFilters,
    pendingNodeGroupFilters,
    pendingDiskTypeFilters,
    pendingInstanceFilters,
    pendingVolumeIdFilters,
    pendingTagFilters,
    pendingCapacityMin,
    pendingCapacityMax,
  ]);

  const resetPendingFilters = useCallback(() => {
    const empty = emptyPanelState(capacityBounds);
    setPendingActivationStatuses(empty.activationStatuses);
    setPendingSupportStatuses(empty.supportStatuses);
    setPendingAccountFilters(empty.accountFilters);
    setPendingClusterFilters(empty.clusterFilters);
    setPendingInfrastructureFilters(empty.infrastructureFilters);
    setPendingRegionFilters(empty.regionFilters);
    setPendingNodeGroupFilters(empty.nodeGroupFilters);
    setPendingDiskTypeFilters(empty.diskTypeFilters);
    setPendingInstanceFilters(empty.instanceFilters);
    setPendingVolumeIdFilters(empty.volumeIdFilters);
    setPendingTagFilters(empty.tagFilters);
    setPendingCapacityMin(empty.capacityMin);
    setPendingCapacityMax(empty.capacityMax);
  }, [capacityBounds]);

  const applyPendingFilters = useCallback(() => {
    return panelStateToExplorationFilters(getPanelState(), capacityBounds);
  }, [getPanelState, capacityBounds]);

  return {
    pendingActivationStatuses,
    setPendingActivationStatuses,
    pendingSupportStatuses,
    setPendingSupportStatuses,
    pendingAccountFilters,
    setPendingAccountFilters,
    pendingClusterFilters,
    setPendingClusterFilters,
    pendingInfrastructureFilters,
    setPendingInfrastructureFilters,
    pendingRegionFilters,
    setPendingRegionFilters,
    pendingNodeGroupFilters,
    setPendingNodeGroupFilters,
    pendingDiskTypeFilters,
    setPendingDiskTypeFilters,
    pendingInstanceFilters,
    setPendingInstanceFilters,
    pendingVolumeIdFilters,
    setPendingVolumeIdFilters,
    pendingTagFilters,
    setPendingTagFilters,
    pendingCapacityMin,
    setPendingCapacityMin,
    pendingCapacityMax,
    setPendingCapacityMax,
    resetPendingFilters,
    applyPendingFilters,
    getPanelState,
  };
}
